function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
// ----------------------------------------------------------------------------
// Product Form Web Component: iamProductPrice <iam-product-price>
// Extends: iamFormElement
// ----------------------------------------------------------------------------
// Displays the product price in the product form.
// - It updates the price display whenever a variant is changed.
// - It uses the section rendering API to fetch the `product-form__price` section for the selected variant.
// ----------------------------------------------------------------------------
// Event Listeners:
// - 'productForm:variant:changed' → Listens for when the product variant is
//                                   changed to trigger updating the price
// ----------------------------------------------------------------------------
// Event Triggers:
// - 'productForm:price:updated'   → Triggered after the price is updated
// ----------------------------------------------------------------------------
// Event Filters:
// - 'productForm:price:updating'  → Allows other modules to modify the price HTML before updating
// ----------------------------------------------------------------------------
// Key Data Mutations:
// - Updates the product price by fetching the new price from the server and replacing
//   the components inner HTML with the new price content.
// ----------------------------------------------------------------------------
// Gotchas:
// - Relies on the AJAX call to fetch the price HTML, so any error in that request will
//   result in an error message (error_retrieving_price) being displayed.
// ----------------------------------------------------------------------------

// Helpers
import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';
import iamFormElement from 'scripts/classes/iamFormElement';
import { sectionRendering } from 'scripts/helpers/iamota-shopify-ajax';
import { getUrlWithVariant } from '@shopify/theme-product-form';
import translate from 'scripts/helpers/iamota-translate';
import { formatMoney } from '@shopify/theme-currency';

// -------------------------------------
// Web Component
// -------------------------------------

class iamProductPrice extends iamFormElement {
  /**
   * Initialize the component when the product form is initialized
   */
  onFormInit() {
    // Bind Event Listeners
    iamotaEventHandler.subscribe('productForm:variant:changed', this.onVariantChange.bind(this), {
      context: this.iamForm,
      source: "product-price.js"
    });

    // Subscribe to selling plan changes
    this.subscribeToSellingPlanChanges();

    // Update the price to the inital value
    this.updatePrice(this.iamForm.getCurrentVariant());
  }

  /**
   * Subscribe to selling plan changes
   */
  subscribeToSellingPlanChanges() {
    var sellingPlanInput = this.iamForm.querySelector('iam-selling-plan-input');
    if (sellingPlanInput) {
      iamotaEventHandler.subscribe('sellingPlanInput:changed', this.onSellingPlanChange.bind(this), {
        context: sellingPlanInput,
        source: "product-price.js"
      });
    } else {
      // If not found, try again after a short delay
      setTimeout(() => {
        var retryInput = this.iamForm.querySelector('iam-selling-plan-input');
        if (retryInput) {
          iamotaEventHandler.subscribe('sellingPlanInput:changed', this.onSellingPlanChange.bind(this), {
            context: retryInput,
            source: "product-price.js"
          });
        }
      }, 100);
    }
  }

  /**
   * Trigger actions when the variant changes
   * @param {variantObject} variant
   */
  onVariantChange(_ref) {
    var {
      variant
    } = _ref;
    this.updatePrice(variant);
  }

  /**
   * Trigger actions when the selling plan changes
   * @param {Object} data - Contains the selected selling plan
   */
  onSellingPlanChange(_ref2) {
    var {
      sellingPlan
    } = _ref2;
    this.updatePriceForSellingPlan(sellingPlan);
  }

  /**
   * Update the price display based on the selected selling plan
   * @param {Object} sellingPlan - The selected selling plan object
   */
  updatePriceForSellingPlan(sellingPlan) {
    var priceContainer = this.querySelector('.product__price');
    if (!priceContainer) return;
    var originalPrice = parseInt(priceContainer.dataset.originalPrice) || 0;
    var originalCompareAtPrice = parseInt(priceContainer.dataset.originalCompareAtPrice) || 0;
    //const discountPercentage = parseFloat(priceContainer.dataset.discountPercentage) || 0;

    // Get the price and compare at price spans
    var priceOriginalSpan = priceContainer.querySelector('.price__original');
    var priceSaleSpan = priceContainer.querySelector('.price__sale');
    if (!priceOriginalSpan || !originalPrice) return;
    var hasSellingPlan = sellingPlan && sellingPlan.purchase_type !== 'onetime';
    var displayPrice = hasSellingPlan && sellingPlan.price ? sellingPlan.price : originalPrice;
    var displayCompareAt = hasSellingPlan && sellingPlan.compare_at_price ? sellingPlan.compare_at_price : originalCompareAtPrice;

    // If subscription is selected and has a discount
    //if (sellingPlan && sellingPlan.purchase_type === 'subscription') {
    // Calculate discounted price
    // const discountedPrice = Math.round(originalPrice * (1 - discountPercentage / 100));

    // Update the displayed price
    priceOriginalSpan.innerHTML = formatMoney(displayPrice);
    // If there's a compare at price, show the original price as strikethrough
    // if (originalCompareAtPrice && originalCompareAtPrice > originalPrice) {
    //     if (priceSaleSpan) {
    //         priceSaleSpan.innerHTML = formatMoney(originalCompareAtPrice);
    //     }
    //     priceOriginalSpan.classList.add('price__original--sale');
    // } else {
    //     // Clear compare at price if not applicable
    //     if (priceSaleSpan) {
    //         priceSaleSpan.innerHTML = '';
    //     }
    //     priceOriginalSpan.classList.remove('price__original--sale');
    // }
    if (hasSellingPlan) {
      if (priceSaleSpan) {
        priceSaleSpan.innerHTML = '';
      }
      priceOriginalSpan.classList.remove('price__original--sale');
      return;
    }

    // Update compare at price if it exists and is valid
    if (displayCompareAt && displayCompareAt > displayPrice) {
      if (priceSaleSpan) {
        priceSaleSpan.innerHTML = formatMoney(displayCompareAt);
      }
      priceOriginalSpan.classList.add('price__original--sale');
    } else {
      // Clear compare at price and sale styling
      if (priceSaleSpan) {
        priceSaleSpan.innerHTML = '';
      }
      priceOriginalSpan.classList.remove('price__original--sale');
    }
  }

  /**
   * Update the price based on the specified variant.
   * @param {variantObject} variant
   */
  updatePrice(variant) {
    var _this = this;
    if (variant) {
      this.setLoading(true);
      sectionRendering.fetchSingle('main-product__price', getUrlWithVariant(window.location.pathname, variant.id)).then(/*#__PURE__*/function () {
        var _ref3 = _asyncToGenerator(function* (sectionContent) {
          var priceHTML = yield iamotaEventHandler.applyFilters('productForm:price:updating', sectionContent, {
            context: _this.iamForm
          });
          _this.innerHTML = priceHTML;

          // After updating the price HTML, check if there's a selected selling plan
          // and update the price accordingly
          var sellingPlanInput = _this.iamForm.querySelector('iam-selling-plan-input');
          if (sellingPlanInput) {
            var currentPlan = sellingPlanInput.getCurrentSellingPlan();
            if (currentPlan && !sellingPlanInput.hideSubscriptionPrice) {
              _this.updatePriceForSellingPlan(currentPlan);
            }
          }
          iamotaEventHandler.trigger('productForm:price:updated', {
            priceHTML
          }, {
            context: _this.iamForm
          });
        });
        return function (_x) {
          return _ref3.apply(this, arguments);
        };
      }()).catch(error => {
        console.error('productForm: Error fetching price section:', error);
        this.innerHTML = translate('An unexpected error occurrred when retrieving the price, please try refreshing the page', 'productForm.error_retrieving_price');
      }).finally(() => {
        this.setLoading(false);
      });
    } else {
      // No variant, hide the price
      this.innerHTML = '';
    }
  }
}
customElements.define('iam-product-price', iamProductPrice);